import { isNil } from "./utils";

export type HsvColor = {
	h: number;
	s: number;
	v: number;
};

export type RgbColor = {
	r: number;
	g: number;
	b: number;
};

export function hsvToRgb(hsv: HsvColor): RgbColor {
	const {
		h,
		s,
		v
	} = hsv;

	if (s === 0) {
		const value = Math.round(v * 255);
		return {
			r: value,
			g: value,
			b: value
		};
	}

	const c: number = v * s;

	const derivedH: number = h / 60;

	const x: number = c * (1 - Math.abs(derivedH % 2 - 1));

	let rgb: RgbColor;
	switch (Math.floor(derivedH) % 6) {
	case 0:
		rgb = {
			r: c,
			g: x,
			b: 0
		};
		break;
	case 1:
		rgb = {
			r: x,
			g: c,
			b: 0
		};
		break;
	case 2:
		rgb = {
			r: 0,
			g: c,
			b: x
		};
		break;
	case 3:
		rgb = {
			r: 0,
			g: x,
			b: c
		};
		break;
	case 4:
		rgb = {
			r: x,
			g: 0,
			b: c
		};
		break;
	case 5:
		rgb = {
			r: c,
			g: 0,
			b: x
		};
		break;
	default:
		throw new Error();
	}

	const m = v - c;

	return {
		r: Math.round((rgb.r + m) * 255),
		g: Math.round((rgb.g + m) * 255),
		b: Math.round((rgb.b + m) * 255)
	};
}

export function rgbToHsv(rgb: RgbColor): HsvColor {
	const {
		r,
		g,
		b
	} = rgb;

	const derivedR = r / 255;
	const derivedG = g / 255;
	const derivedB = b / 255;

	const maxValue = Math.max(derivedR, derivedG, derivedB);
	const minValue = Math.min(derivedR, derivedG, derivedB);

	const deltaValue = maxValue - minValue;

	let h: number;

	if (deltaValue === 0) {
		h = 0;
	} else {
		switch (maxValue) {
			case derivedR:
				h = ((derivedG - derivedB) / deltaValue) + (derivedG < derivedB ? 6 : 0);
				break;
			case derivedG:
				h = ((derivedB - derivedR) / deltaValue) + 2;
				break;
			case derivedB:
				h = ((derivedR - derivedG) / deltaValue) + 4;
				break;
			default:
				throw new Error();
		}
	}

	h = h * 60;

	const s: number = maxValue === 0
		? 0
		: deltaValue / maxValue;
	const v = maxValue;

	return {
		h,
		s,
		v
	};
}

function componentToHex(c: number): string {
	const hex: string = c.toString(16);
	return hex.length === 1
		? "0" + hex
		: hex;
}

export function rgbToHex(rgb: RgbColor): string {
	return `#${componentToHex(rgb.r)}${componentToHex(rgb.g)}${componentToHex(rgb.b)}`;
}

const predefinedColorMap: Record<string, string> = {
	aliceblue: "#f0f8ff",
	antiquewhite: "#faebd7",
	aqua: "#00ffff",
	aquamarine: "#7fffd4",
	azure: "#f0ffff",
	beige: "#f5f5dc",
	bisque: "#ffe4c4",
	black: "#000000",
	blanchedalmond: "#ffebcd",
	blue: "#0000ff",
	blueviolet: "#8a2be2",
	brown: "#a52a2a",
	burlywood: "#deb887",
	cadetblue: "#5f9ea0",
	chartreuse: "#7fff00",
	chocolate: "#d2691e",
	coral: "#ff7f50",
	cornflowerblue: "#6495ed",
	cornsilk: "#fff8dc",
	crimson: "#dc143c",
	cyan: "#00ffff",
	darkblue: "#00008b",
	darkcyan: "#008b8b",
	darkgoldenrod: "#b8860b",
	darkgray: "#a9a9a9",
	darkgrey: "#a9a9a9",
	darkgreen: "#006400",
	darkkhaki: "#bdb76b",
	darkmagenta: "#8b008b",
	darkolivegreen: "#556b2f",
	darkorange: "#ff8c00",
	darkorchid: "#9932cc",
	darkred: "#8b0000",
	darksalmon: "#e9967a",
	darkseagreen: "#8fbc8f",
	darkslateblue: "#483d8b",
	darkslategray: "#2f4f4f",
	darkslategrey: "#2f4f4f",
	darkturquoise: "#00ced1",
	darkviolet: "#9400d3",
	deeppink: "#ff1493",
	deepskyblue: "#00bfff",
	dimgray: "#696969",
	dimgrey: "#696969",
	dodgerblue: "#1e90ff",
	firebrick: "#b22222",
	floralwhite: "#fffaf0",
	forestgreen: "#228b22",
	fuchsia: "#ff00ff",
	gainsboro: "#dcdcdc",
	ghostwhite: "#f8f8ff",
	gold: "#ffd700",
	goldenrod: "#daa520",
	gray: "#808080",
	grey: "#808080",
	green: "#008000",
	greenyellow: "#adff2f",
	honeydew: "#f0fff0",
	hotpink: "#ff69b4",
	indianred: "#cd5c5c",
	indigo: "#4b0082",
	ivory: "#fffff0",
	khaki: "#f0e68c",
	lavender: "#e6e6fa",
	lavenderblush: "#fff0f5",
	lawngreen: "#7cfc00",
	lemonchiffon: "#fffacd",
	lightblue: "#add8e6",
	lightcoral: "#f08080",
	lightcyan: "#e0ffff",
	lightgoldenrodyellow: "#fafad2",
	lightgray: "#d3d3d3",
	lightgrey: "#d3d3d3",
	lightgreen: "#90ee90",
	lightpink: "#ffb6c1",
	lightsalmon: "#ffa07a",
	lightseagreen: "#20b2aa",
	lightskyblue: "#87cefa",
	lightslategray: "#778899",
	lightslategrey: "#778899",
	lightsteelblue: "#b0c4de",
	lightyellow: "#ffffe0",
	lime: "#00ff00",
	limegreen: "#32cd32",
	linen: "#faf0e6",
	magenta: "#ff00ff",
	maroon: "#800000",
	mediumaquamarine: "#66cdaa",
	mediumblue: "#0000cd",
	mediumorchid: "#ba55d3",
	mediumpurple: "#9370d8",
	mediumseagreen: "#3cb371",
	mediumslateblue: "#7b68ee",
	mediumspringgreen: "#00fa9a",
	mediumturquoise: "#48d1cc",
	mediumvioletred: "#c71585",
	midnightblue: "#191970",
	mintcream: "#f5fffa",
	mistyrose: "#ffe4e1",
	moccasin: "#ffe4b5",
	navajowhite: "#ffdead",
	navy: "#000080",
	oldlace: "#fdf5e6",
	olive: "#808000",
	olivedrab: "#6b8e23",
	orange: "#ffa500",
	orangered: "#ff4500",
	orchid: "#da70d6",
	palegoldenrod: "#eee8aa",
	palegreen: "#98fb98",
	paleturquoise: "#afeeee",
	palevioletred: "#d87093",
	papayawhip: "#ffefd5",
	peachpuff: "#ffdab9",
	peru: "#cd853f",
	pink: "#ffc0cb",
	plum: "#dda0dd",
	powderblue: "#b0e0e6",
	purple: "#800080",
	red: "#ff0000",
	rosybrown: "#bc8f8f",
	royalblue: "#4169e1",
	saddlebrown: "#8b4513",
	salmon: "#fa8072",
	sandybrown: "#f4a460",
	seagreen: "#2e8b57",
	seashell: "#fff5ee",
	sienna: "#a0522d",
	silver: "#c0c0c0",
	skyblue: "#87ceeb",
	slateblue: "#6a5acd",
	slategray: "#708090",
	slategrey: "#708090",
	snow: "#fffafa",
	springgreen: "#00ff7f",
	steelblue: "#4682b4",
	tan: "#d2b48c",
	teal: "#008080",
	thistle: "#d8bfd8",
	tomato: "#ff6347",
	turquoise: "#40e0d0",
	violet: "#ee82ee",
	wheat: "#f5deb3",
	white: "#ffffff",
	whitesmoke: "#f5f5f5",
	yellow: "#ffff00",
	yellowgreen: "#9acd32"
};

const HEX_STRING_REGEX: RegExp = /^#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/;

export function hexToRgb(hex: string): RgbColor | undefined {
	if (!hex) {
		return undefined;
	}

	const isValidHexString: boolean = HEX_STRING_REGEX.test(hex);
	if (!isValidHexString)
	{
		return undefined;
	}

	if (hex.length === 4) {
		return {
			r: parseInt(hex[1] + hex[1], 16),
			g: parseInt(hex[2] + hex[2], 16),
			b: parseInt(hex[3] + hex[3], 16)
		};
	} else if (hex.length === 7) {
		return {
			r: parseInt(hex.substring(1, 3), 16),
			g: parseInt(hex.substring(3, 5), 16),
			b: parseInt(hex.substring(5, 7), 16)
		};
	}

	return undefined;
}

export function cssColorToRgb(cssColor: string | undefined): RgbColor | undefined {
	if (!cssColor || cssColor.length === 0) {
		return undefined;
	}

	if (cssColor[0] === "#") {
		return hexToRgb(cssColor);
	}

	const caseInsensitiveColor: string = cssColor.toLowerCase();

	if (caseInsensitiveColor in predefinedColorMap) {
		return hexToRgb(predefinedColorMap[caseInsensitiveColor]);
	}

	return undefined;
}

export function hsvToHex(hsv: HsvColor | undefined): string | undefined {
	if (!hsv) {
		return undefined;
	}

	const rgb: RgbColor = hsvToRgb(hsv);
	return rgbToHex(rgb);
}

export function cssColorToHsv(cssColor: string | nil): HsvColor | undefined {
	if (!cssColor) {
		return undefined;
	}

	const rgb: RgbColor | undefined = cssColorToRgb(cssColor);
	if (!rgb) {
		return undefined;
	}
	return rgbToHsv(rgb);
}

// Algorithm and constants are taken from here
// https://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef
export function calculateRelativeLuminance(rgb: RgbColor): number {
	const adjustGamma = (colorComponent: number): number => {
		colorComponent = colorComponent / 255;

		if (colorComponent <= 0.03928) {
			return colorComponent / 12.92;
		} else {
			return Math.pow((colorComponent + 0.055) / 1.055, 2.4);
		}
	};

	const r: number = adjustGamma(rgb.r);
	const g: number = adjustGamma(rgb.g);
	const b: number = adjustGamma(rgb.b);

	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function getContrastRatio(
	color1: RgbColor,
	color2: RgbColor
): number;
export function getContrastRatio(
	color1: RgbColor,
	precalculatedLuminance: number
): number;
export function getContrastRatio(
	color1: RgbColor,
	color2OrPrecalculatedLuminance: RgbColor | number
): number {

	let l1: number = calculateRelativeLuminance(color1);
	let l2: number = typeof color2OrPrecalculatedLuminance === "number"
		? color2OrPrecalculatedLuminance
		: calculateRelativeLuminance(color2OrPrecalculatedLuminance);

	if (l1 < l2) {
		const temp: number = l1;
		l1 = l2;
		l2 = temp;
	}

	return (l1 + 0.05) / (l2 + 0.05);
}

export type RgbaColor = RgbColor & {
	a: number;
};

export function parseComputedColor(value: string): RgbaColor | undefined {
	const result: RgbColor | undefined = cssColorToRgb(value);

	if (result) {
		return {
			...result,
			a: 1
		};
	}

	if (value.startsWith("rgb(")) {
		const [red, green, blue] =value.substring(4, value.length - 1)
			.split(",");

		return {
			r: parseInt(red, 10),
			g: parseInt(green, 10),
			b: parseInt(blue, 10),
			a: 1
		};
	}

	if (value.startsWith("rgba(")) {
		const [red, green, blue, alpha] = value.substring(5, value.length - 1)
			.split(",");

		return {
			r: parseInt(red, 10),
			g: parseInt(green, 10),
			b: parseInt(blue, 10),
			a: parseFloat(alpha)
		};
	}

	if (value.startsWith("color(")) {
		// color(srgb 0.974118 0.807059 0.865882)
		const [
			colorSpace,
			c1,
			c2,
			c3,
			_,
			alpha
		] = value.substring(6, value.length - 1)
			.split(" ");

		switch (colorSpace) {
			case "srgb":
				return {
					r: Math.round(parseFloat(c1) * 255),
					g: Math.round(parseFloat(c2) * 255),
					b: Math.round(parseFloat(c3) * 255),
					a: isNil(alpha)
						? 1
						: parseFloat(alpha)
				};
			default:
				break;
		}
	}

	console.warn(`Unsupported serialized color format '${value}'`);
	return undefined;
}

export function rgbaToHex(color: RgbaColor): string {
	let result: string = rgbToHex(color);
	if (color.a < 1) {
		const hexAlpha: string = Math.round(255 * color.a)
			.toString(16);

		result += hexAlpha.length === 1
			? "0" + hexAlpha
			: hexAlpha;
	}

	return result;
}